/**
 * backfillFontScriptCoverage.ts — PLAT-11467 (epic PLAT-11407)
 *
 * Fills in `scriptCoverage` for brand fonts uploaded before PLAT-11412.
 *
 * PLAT-11412 probes a font file's cmap at UPLOAD time and persists which
 * caption scripts it covers. Everything uploaded earlier has no coverage at
 * all — measured on the dev environment, that is **every** font: 546 of 546
 * variants across 11 workspaces, DIN Next LT Pro (84 variants) included.
 *
 * Coverage absent means "unknown", and both features built on it treat unknown
 * as silence — deliberately, so a font we never probed is never falsely
 * flagged. The consequence is that until this backfill runs, PLAT-11412's
 * coverage warnings and PLAT-11466's translated-graphics font correction are
 * BOTH dormant for every existing workspace: correct code with no data to act
 * on.
 *
 * The font file is the source of truth and is already stored (each variant
 * carries its `source` URL), so coverage is recoverable without re-uploading
 * anything.
 *
 * Pure orchestration: fetching and parsing are injected, so this is testable
 * with neither network nor font binaries. No MobX, no store, no IO of its own.
 */

import { ok } from '@src/services/common/Result';
import { extractErrorInfo } from '@src/utility/error';
import { detectFontScriptCoverage } from '@src/utility/fontScriptCoverage';
import { LogManager } from '@src/utility/Logger';

import type { Result } from '@src/services/common/Result';
import type { BrandingCustomFont } from '@src/types.ts/story';
import type { FontGlyphLookup } from '@src/utility/fontScriptCoverage';

const logger = LogManager.getLogger('service:BrandFontCoverage');

/** How many font files to pull at once — enough to be quick, few enough to be polite. */
const DEFAULT_CONCURRENCY = 4;

export interface FontCoverageProbeDeps {
  /** Fetch a font file by its stored `source` URL. */
  fetchFontBuffer: (url: string) => Promise<ArrayBuffer>;
  /** Parse a font binary into something that can answer glyph lookups. */
  parseFont: (buffer: ArrayBuffer) => FontGlyphLookup;
}

export interface BackfillOptions {
  /**
   * Restrict the probe to the fonts actually needed right now (e.g. the
   * families the open video renders). Omitted = every variant missing
   * coverage.
   */
  shouldProbe?: (font: BrandingCustomFont) => boolean;
  concurrency?: number;
}

export interface BackfillResult {
  /**
   * The full list with coverage filled in where it could be probed. Entries
   * that were not probed — or whose probe failed — keep their ORIGINAL
   * reference, so a caller can diff by identity.
   */
  fonts: BrandingCustomFont[];
  /** How many variants gained coverage. `0` means nothing to persist. */
  probed: number;
  /** How many probes failed (unreachable file, unparseable binary). */
  failed: number;
}

/**
 * Probe every variant that is missing `scriptCoverage` and return the updated
 * list.
 *
 * Failure is per-font and never fatal: a font whose file 404s or will not
 * parse simply stays unknown, which is exactly the state it was already in.
 * The overall call fails only if something is structurally wrong (nothing to
 * work with), so a caller can treat `ok: false` as "don't persist".
 */
export async function backfillFontScriptCoverage(
  fonts: readonly BrandingCustomFont[] | null | undefined,
  deps: FontCoverageProbeDeps,
  options: BackfillOptions = {},
): Promise<Result<BackfillResult>> {
  if (!fonts?.length) {
    return ok({ fonts: [], probed: 0, failed: 0 });
  }

  const { shouldProbe, concurrency = DEFAULT_CONCURRENCY } = options;
  const next = [...fonts];

  // Index every variant needing a probe. An archived font is included: an
  // element can still reference it, and its coverage is just as true.
  const targets: number[] = [];
  for (let i = 0; i < next.length; i++) {
    const font = next[i];
    if (font.scriptCoverage) continue;
    if (!font.source) continue;
    if (shouldProbe && !shouldProbe(font)) continue;
    targets.push(i);
  }
  if (targets.length === 0) {
    return ok({ fonts: next, probed: 0, failed: 0 });
  }

  // One fetch per distinct URL: a family's weights are separate files, but the
  // same variant can legitimately appear twice across families.
  const coverageByUrl = new Map<
    string,
    ReturnType<typeof detectFontScriptCoverage> | null
  >();
  const urls = [...new Set(targets.map(i => next[i].source))];

  let cursor = 0;
  const worker = async () => {
    for (;;) {
      const index = cursor++;
      if (index >= urls.length) return;
      const url = urls[index];
      try {
        const parsed = deps.parseFont(await deps.fetchFontBuffer(url));
        coverageByUrl.set(url, detectFontScriptCoverage(parsed));
      } catch (error) {
        // Stays unknown — the same state as before, never a false claim.
        coverageByUrl.set(url, null);
        logger.warn('Font coverage probe failed; leaving it unknown', {
          url,
          ...extractErrorInfo(error),
        });
      }
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(concurrency, urls.length) }, worker),
  );

  let probed = 0;
  let failed = 0;
  for (const i of targets) {
    const coverage = coverageByUrl.get(next[i].source);
    if (coverage) {
      next[i] = { ...next[i], scriptCoverage: coverage };
      probed += 1;
    } else {
      failed += 1;
    }
  }

  logger.info('Brand font coverage backfill complete', {
    considered: targets.length,
    probed,
    failed,
  });
  return ok({ fonts: next, probed, failed });
}
